import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../../common/constants/error';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { CompartmentListFacade } from '../../list/compartment-list.facade';
import { CompartmentOverview, CompartmentSapModel } from '../../models';
import {
  CompartmentOverviewInitialState,
  CompartmentOverviewLoad,
  CompartmentOverviewUpdate,
  SetInitialErrorState,
} from './compartment-overview.actions';
import { CompartmentOverviewService } from './compartment-overview.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface CompartmentOverviewStateModel {
  overview: CompartmentOverview;
  loaded: boolean;
  error: boolean;
}

const initialState: CompartmentOverviewStateModel = {
  overview: null,
  loaded: false,
  error: false,
};

@State<CompartmentOverviewStateModel>({
  name: 'compartmentOverview',
  defaults: initialState,
})
@Injectable()
export class CompartmentOverviewState {
  constructor(
    private compartmentOverviewService: CompartmentOverviewService,
    private compartmentListFacade: CompartmentListFacade,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: CompartmentOverviewStateModel): CompartmentOverviewStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: CompartmentOverviewStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getName(state: CompartmentOverviewStateModel): string {
    return state.overview?.name;
  }

  @Selector()
  static getOverview(state: CompartmentOverviewStateModel): CompartmentOverview {
    return state.overview;
  }

  @Selector()
  static getCompartmentSapClientViews(state: CompartmentOverviewStateModel): CompartmentSapModel[] {
    return state.overview.compartmentSapClientViews;
  }

  @Selector()
  static getError(state: CompartmentOverviewStateModel): boolean {
    return state.error;
  }

  @Action(CompartmentOverviewLoad)
  getCompartmentOverview(
    ctx: StateContext<CompartmentOverviewStateModel>,
    { payload }: CompartmentOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentOverviewService.getOverviewData(payload).pipe(
      map((overview: CompartmentOverview) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentOverviewUpdate)
  updateCompartmentOverview(
    ctx: StateContext<CompartmentOverviewStateModel>,
    { payload }: CompartmentOverviewUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.compartmentOverviewService.updateCompartment(payload).pipe(
      take(1),
      map((response: CompartmentOverview) => {
        this.notificationService.success('api.compartment.success.update');

        ctx.patchState({
          overview: response,
        });

        if (payload.listRefreshNeeded) {
          this.compartmentListFacade.loadAll();
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        ctx.patchState({
          error: true,
        });
        return of(this.notificationService.error(error?.message));
      }),
    );
  }

  @Action(CompartmentOverviewInitialState)
  setInitialState(ctx: StateContext<CompartmentOverviewStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(SetInitialErrorState)
  resetErrorState(ctx: StateContext<CompartmentOverviewStateModel>): void {
    ctx.patchState({
      error: false,
    });
  }
}
